import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt, useBalance } from 'wagmi';
import { formatEther } from 'viem';
import { AppSidebar } from "@/components/app-sidebar";
import { RiImageLine, RiLoader4Line, RiWallet3Line, RiShoppingCartLine, RiCloseLine } from "@remixicon/react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { Button } from "@/components/button";
import { Card, CardContent } from "@/components/card";
import { Badge } from "@/components/badge";
import { NFT_MARKETPLACE_CONTRACT_CONFIG } from '@/configs/nft-marketplace.config';
import { ERC721_ABI } from '@/abis/erc721';
import type { Listing } from '@/abis/nft-marketplace';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import ListNftModal from "@/components/list-nft-modal";
import { RiAddLine } from "@remixicon/react";

// Note: Using getUserAndAllOtherListings function - no need for MAX_LISTING_IDS anymore

interface ListingWithMetadata extends Listing {
  imageUrl?: string | null;
  metadata?: any;
}

export default function MarketplacePage() {
  const { address: userAccount } = useAccount();
  const [activeTab, setActiveTab] = useState<'my-listings' | 'marketplace'>('marketplace');
  const [listingsData, setListingsData] = useState<Record<string, ListingWithMetadata>>({});
  const [buyingListingId, setBuyingListingId] = useState<bigint | null>(null);
  const [cancellingListingId, setCancellingListingId] = useState<bigint | null>(null);
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>(undefined);
  const [isListModalOpen, setIsListModalOpen] = useState(false);

  // Debug state
  const [debugInfo, setDebugInfo] = useState({
    contractAddress: NFT_MARKETPLACE_CONTRACT_CONFIG.address,
    userAccount: userAccount || null,
    userListingsCount: 0,
    marketplaceListingsCount: 0,
    listingsDataCount: 0,
    errors: [] as string[],
  });

  const { writeContractAsync, isPending: isWritePending } = useWriteContract();
  const { isLoading: isTxPending, isSuccess: isTxSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
    query: {
      enabled: !!txHash,
    },
  });

  // Get user balance
  const { data: balance } = useBalance({
    address: userAccount,
  });

  // Debug: Log contract address and user account
  useEffect(() => {
    console.log('🔍 [Marketplace Debug] Contract Address:', NFT_MARKETPLACE_CONTRACT_CONFIG.address);
    console.log('🔍 [Marketplace Debug] User Account:', userAccount);
    console.log('🔍 [Marketplace Debug] Contract Address Valid:', !!NFT_MARKETPLACE_CONTRACT_CONFIG.address && NFT_MARKETPLACE_CONTRACT_CONFIG.address !== '0x');

    setDebugInfo(prev => ({
      ...prev,
      contractAddress: NFT_MARKETPLACE_CONTRACT_CONFIG.address,
      userAccount: userAccount || null,
    }));
  }, [userAccount]);

  // Fetch all listings using the new efficient function
  // Use a dummy address (zero address) when user is not connected to get all listings
  const { data: listingsData_raw, isLoading: isLoadingListings, refetch: refetchListings } = useReadContract({
    address: NFT_MARKETPLACE_CONTRACT_CONFIG.address,
    abi: NFT_MARKETPLACE_CONTRACT_CONFIG.abi,
    functionName: 'getUserAndAllOtherListings',
    args: userAccount ? [userAccount] : ['0x0000000000000000000000000000000000000000' as `0x${string}`],
    query: {
      enabled: !!NFT_MARKETPLACE_CONTRACT_CONFIG.address,
    },
  }) as {
    data: [Listing[], Listing[]] | undefined;
    isLoading: boolean;
    refetch: () => void;
  };

  // Process listings data and fetch metadata
  useEffect(() => {
    if (!listingsData_raw) {
      setListingsData({});
      setDebugInfo(prev => ({
        ...prev,
        userListingsCount: 0,
        marketplaceListingsCount: 0,
        listingsDataCount: 0,
      }));
      return;
    }

    const [userListings, otherListings] = listingsData_raw;
    const allListings = [...userListings, ...otherListings];

    console.log(`✅ [Marketplace Debug] Fetched listings:`, {
      userListings: userListings.length,
      otherListings: otherListings.length,
      total: allListings.length,
    });

    // Update listings data, preserving existing metadata
    setListingsData(prev => {
      const newListingsData: Record<string, ListingWithMetadata> = {};

      allListings.forEach((listing) => {
        const listingIdStr = listing.id.toString();
        const existing = prev[listingIdStr];

        newListingsData[listingIdStr] = {
          ...listing,
          imageUrl: existing?.imageUrl ?? null,
          metadata: existing?.metadata ?? null,
        };
      });

      return newListingsData;
    });

    // Update debug info
    setDebugInfo(prev => ({
      ...prev,
      userListingsCount: userListings.length,
      marketplaceListingsCount: otherListings.length,
      listingsDataCount: allListings.length,
    }));
  }, [listingsData_raw]);

  // Track metadata fetches in progress to prevent duplicates (using ref to avoid re-renders)
  const fetchingMetadataRef = useRef<Set<string>>(new Set());

  // Component to fetch tokenURI for each listing
  const TokenURIFetcher = ({ listing }: { listing: Listing }) => {
    const { data: tokenURI } = useReadContract({
      address: listing.contractAddress,
      abi: ERC721_ABI,
      functionName: 'tokenURI',
      args: [listing.tokenId],
      query: {
        enabled: !!listing.contractAddress && !!listing.tokenId,
      },
    }) as { data: string | undefined };

    useEffect(() => {
      if (tokenURI) {
        fetchMetadata(listing.id, tokenURI, listing.contractAddress);
      }
    }, [tokenURI, listing.id, listing.contractAddress]);

    return null;
  };

  // Fetch NFT metadata from tokenURI (using useCallback to prevent recreation)
  const fetchMetadata = useCallback(async (listingId: bigint, tokenURI: string, contractAddress: `0x${string}`) => {
    const listingIdStr = listingId.toString();

    // Check if we're already fetching this metadata
    if (fetchingMetadataRef.current.has(listingIdStr)) {
      return; // Already fetching, skip
    }

    // Mark as fetching
    fetchingMetadataRef.current.add(listingIdStr);

    try {
      console.log(`🔍 [Marketplace Debug] Fetching metadata for listing ${listingId}, tokenURI:`, tokenURI);
      let url = tokenURI;
      if (url.startsWith('ipfs://')) {
        url = `https://ipfs.io/ipfs/${url.replace('ipfs://', '')}`;
        console.log(`🔍 [Marketplace Debug] Converted IPFS URL to:`, url);
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      let metadata: any;

      if (contentType && contentType.includes('application/json')) {
        metadata = await response.json();
      } else {
        const text = await response.text();
        try {
          metadata = JSON.parse(text);
        } catch (parseError) {
          throw new Error('Response is not valid JSON');
        }
      }

      console.log(`✅ [Marketplace Debug] Metadata fetched for listing ${listingId}:`, metadata);

      // Extract image URL
      let imageUrl: string | null = null;
      if (metadata && metadata.image) {
        imageUrl = metadata.image;
        if (imageUrl != null && imageUrl.startsWith('ipfs://')) {
          imageUrl = `https://ipfs.io/ipfs/${imageUrl.replace('ipfs://', '')}`;
        }
        console.log(`✅ [Marketplace Debug] Image URL for listing ${listingId}:`, imageUrl);
      }

      setListingsData(prev => ({
        ...prev,
        [listingIdStr]: {
          ...prev[listingIdStr],
          imageUrl,
          metadata,
        },
      }));
    } catch (error) {
      console.error(`❌ [Marketplace Debug] Error fetching metadata for listing ${listingId}:`, error);
      setDebugInfo(prev => ({
        ...prev,
        errors: [...prev.errors.slice(-9), `Metadata fetch failed for listing ${listingId}: ${error instanceof Error ? error.message : 'Unknown error'}`],
      }));
      setListingsData(prev => ({
        ...prev,
        [listingIdStr]: {
          ...prev[listingIdStr],
          imageUrl: null,
          metadata: null,
        },
      }));
    } finally {
      // Remove from fetching set
      fetchingMetadataRef.current.delete(listingIdStr);
    }
  }, []);

  // Get user's active listings from fetched data
  const userListings = useMemo(() => {
    if (!listingsData_raw || !userAccount) return [];

    const [userListings_raw] = listingsData_raw;
    return userListings_raw.map(listing => {
      const listingIdStr = listing.id.toString();
      return {
        ...listing,
        imageUrl: listingsData[listingIdStr]?.imageUrl ?? null,
        metadata: listingsData[listingIdStr]?.metadata ?? null,
      } as ListingWithMetadata;
    });
  }, [listingsData_raw, listingsData, userAccount]);

  // Get all active marketplace listings (excluding user's own)
  const marketplaceListings = useMemo(() => {
    if (!listingsData_raw) return [];

    const [, otherListings_raw] = listingsData_raw;
    return otherListings_raw.map(listing => {
      const listingIdStr = listing.id.toString();
      return {
        ...listing,
        imageUrl: listingsData[listingIdStr]?.imageUrl ?? null,
        metadata: listingsData[listingIdStr]?.metadata ?? null,
      } as ListingWithMetadata;
    });
  }, [listingsData_raw, listingsData]);

  // Handle buy NFT
  const handleBuyNFT = async (listingId: bigint, price: bigint) => {
    if (!userAccount) {
      alert('Please connect your wallet');
      return;
    }

    if (balance && balance.value < price) {
      alert('Insufficient balance');
      return;
    }

    try {
      setBuyingListingId(listingId);
      setTxHash(undefined);

      const hash = await writeContractAsync({
        address: NFT_MARKETPLACE_CONTRACT_CONFIG.address,
        abi: NFT_MARKETPLACE_CONTRACT_CONFIG.abi,
        functionName: 'buyNFT',
        args: [listingId],
        value: price,
      });

      setTxHash(hash);
    } catch (error) {
      console.error('Error buying NFT:', error);
      alert(error instanceof Error ? error.message : 'Failed to buy NFT');
      setBuyingListingId(null);
    }
  };

  // Handle cancel listing
  const handleCancelListing = async (listingId: bigint) => {
    if (!userAccount) {
      alert('Please connect your wallet');
      return;
    }

    try {
      setCancellingListingId(listingId);
      setTxHash(undefined);

      const hash = await writeContractAsync({
        address: NFT_MARKETPLACE_CONTRACT_CONFIG.address,
        abi: NFT_MARKETPLACE_CONTRACT_CONFIG.abi,
        functionName: 'cancelListing',
        args: [listingId],
      });

      setTxHash(hash);
    } catch (error) {
      console.error('Error cancelling listing:', error);
      alert(error instanceof Error ? error.message : 'Failed to cancel listing');
      setCancellingListingId(null);
    }
  };

  // Debug info is updated in the useEffect that processes listingsData_raw

  // Reset transaction state on success
  useEffect(() => {
    if (isTxSuccess) {
      setTxHash(undefined);
      setBuyingListingId(null);
      setCancellingListingId(null);
    }
  }, [isTxSuccess]);

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Render listing card
  const renderListingCard = (listing: ListingWithMetadata, isUserListing: boolean = false) => {
    const isBuying = buyingListingId === listing.id;
    const isCancelling = cancellingListingId === listing.id;
    const isProcessing = isBuying || isCancelling || (isWritePending && (buyingListingId === listing.id || cancellingListingId === listing.id));

    return (
      <Card
        key={listing.id.toString()}
        className="bg-sidebar border border-border hover:border-[#FF8A3C]/50 transition-colors"
      >
        <CardContent className="p-4">
          <div className="bg-muted rounded-lg mb-3 flex items-center justify-center overflow-hidden">
            {listing.imageUrl ? (
              <img
                src={listing.imageUrl}
                alt={`NFT #${listing.tokenId.toString()}`}
                className="w-full h-full object-contain"
                onError={() => {
                  setListingsData(prev => ({
                    ...prev,
                    [listing.id.toString()]: {
                      ...prev[listing.id.toString()],
                      imageUrl: null,
                    },
                  }));
                }}
              />
            ) : (
              <RiImageLine className="size-16 text-muted-foreground" />
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Token ID: #{listing.tokenId.toString()}</span>
              <Badge variant="outline" className="bg-[#FF8A3C]/10 text-[#FF8A3C] border-[#FF8A3C]">
                {formatEther(listing.price)} LRS
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              Seller: {formatAddress(listing.seller)}
            </div>
            <div className="text-xs text-muted-foreground">
              Contract: {formatAddress(listing.contractAddress)}
            </div>
            {isUserListing ? (
              <Button
                variant="destructive"
                size="sm"
                className="w-full mt-2"
                onClick={() => handleCancelListing(listing.id)}
                disabled={isProcessing}
              >
                {isCancelling ? (
                  <>
                    <RiLoader4Line className="size-4 animate-spin mr-2" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <RiCloseLine className="size-4 mr-2" />
                    Cancel Listing
                  </>
                )}
              </Button>
            ) : (
              <Button
                size="sm"
                className="w-full mt-2 bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                onClick={() => handleBuyNFT(listing.id, listing.price)}
                disabled={isProcessing || (balance && balance.value < listing.price)}
              >
                {isBuying ? (
                  <>
                    <RiLoader4Line className="size-4 animate-spin mr-2" />
                    Buying...
                  </>
                ) : (
                  <>
                    <RiShoppingCartLine className="size-4 mr-2" />
                    Buy NFT
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/nfts">NFTs</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Marketplace</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ConnectButton />
              </div>
            </header>

            <div className="py-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                  <h1 className="text-3xl font-bold">NFT Marketplace</h1>
                </div>
                {userAccount && (
                  <Button
                    onClick={() => setIsListModalOpen(true)}
                    className="bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
                  >
                    <RiAddLine className="size-4 mr-2" />
                    List your NFT for sell
                  </Button>
                )}
              </div>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              {/* Fetch tokenURI for each listing */}
              {listingsData_raw && (() => {
                const [userListings, otherListings] = listingsData_raw;
                const allListings = [...userListings, ...otherListings];
                return allListings.map((listing) => (
                  <TokenURIFetcher key={listing.id.toString()} listing={listing} />
                ));
              })()}

              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'my-listings' | 'marketplace')} className="w-full">
                <TabsList className="grid w-full max-w-md grid-cols-2">
                  <TabsTrigger value="marketplace">Marketplace</TabsTrigger>
                  <TabsTrigger value="my-listings">My Listings</TabsTrigger>
                </TabsList>

                <TabsContent value="marketplace" className="mt-6">
                  {isLoadingListings ? (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-12 text-center">
                        <RiLoader4Line className="size-16 animate-spin text-[#FF8A3C] mx-auto mb-4" />
                        <p className="text-muted-foreground">Loading marketplace listings...</p>
                      </CardContent>
                    </Card>
                  ) : marketplaceListings.length === 0 ? (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-12 text-center">
                        <RiShoppingCartLine className="size-16 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No active listings in the marketplace</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {marketplaceListings.map(listing => renderListingCard(listing, false))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="my-listings" className="mt-6">
                  {!userAccount ? (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-12 text-center">
                        <RiWallet3Line className="size-16 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">Please connect your wallet to view your listings</p>
                      </CardContent>
                    </Card>
                  ) : isLoadingListings ? (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-12 text-center">
                        <RiLoader4Line className="size-16 animate-spin text-[#FF8A3C] mx-auto mb-4" />
                        <p className="text-muted-foreground">Loading your listings...</p>
                      </CardContent>
                    </Card>
                  ) : userListings.length === 0 ? (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-12 text-center">
                        <RiImageLine className="size-16 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">You don't have any active listings</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {userListings.map(listing => renderListingCard(listing, true))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </SidebarInset>

      {/* List NFT Modal */}
      <ListNftModal
        isOpen={isListModalOpen}
        onClose={() => setIsListModalOpen(false)}
        onSuccess={() => {
          // Refetch listings after successful listing
          refetchListings();
        }}
      />
    </SidebarProvider>
  );
}
